'use client';

import { useEffect, useRef } from 'react';
import { useAddressBook, pickDefaultAddress } from '@/hooks/use-address-book';
import { applySavedAddress, findSavedByPlace, fillDetailsFromSaved } from '@/lib/saved-address';
import { useAddressStore, selectAddressHasHydrated, selectSelectedCity } from '@/stores/address-store';

/**
 * Подставляет основной адрес из книги тому, у кого адрес ещё не выбран.
 *
 * Без этого адресная книга была бы бесполезна там, где она нужнее всего:
 * гейт корзины (openCheckout) требует адрес ДО того, как человек увидит корзину,
 * и сохранённый адрес просто не успевал бы примениться.
 *
 * Правила, чтобы не спорить с человеком:
 *  - только если в сторе адреса НЕТ (свой выбор всегда важнее сохранённого);
 *  - только после гидрации persist'а, иначе решение принималось бы по пустому
 *    состоянию и затирало бы адрес, который вот-вот поднимется из localStorage;
 *  - только когда известен город: без него checkAddress не считает зону вовсе;
 *  - ровно один раз за жизнь вкладки.
 */
export function SavedAddressAutofill() {
  const { addresses } = useAddressBook();
  const hasHydrated = useAddressStore(selectAddressHasHydrated);
  const selectedCity = useAddressStore(selectSelectedCity);
  const doneRef = useRef(false);
  const citiesRequestedRef = useRef(false);

  useEffect(() => {
    if (doneRef.current) return;
    if (!hasHydrated) return;
    if (addresses.length === 0) return;
    const selected = useAddressStore.getState().selectedAddress;
    if (selected) {
      doneRef.current = true;

      /*
       * АДРЕС ВЫБРАН САМ — НО КНИГУ ЭТО НЕ ОТМЕНЯЕТ.
       *
       * Здесь стоял простой return: «адрес уже есть, подставлять нечего».
       * На деле подставлять было чего. Человек выбирает дом на карте — в сторе
       * оказывается только СТРОКА адреса, без квартиры, подъезда и этажа.
       * Книга при этом лежит рядом, с теми же полями, заполненными раньше.
       * И оформление честно спрашивало их заново.
       *
       * Жалоба клиента дословно: «выбрать на карте адрес с точностью до
       * квартиры, затем ещё раз заполнить квартиру, а затем, на этапе
       * оформления заказа выбрав созданный адрес, снова заполнять номер
       * квартиры, этаж, подъезд».
       *
       * Адрес НЕ трогаем — он выбран человеком, и его выбор важнее. Берём из
       * книги только уточнения и только в пустые поля.
       */
      const match = findSavedByPlace(addresses, selected.address);
      if (match) fillDetailsFromSaved(match);
      return;
    }

    // Города нет — зону посчитать не по чему (см. checkAddress). Загружаем
    // список один раз и ждём: эффект перезапустится, когда город появится.
    if (!selectedCity) {
      if (!citiesRequestedRef.current) {
        citiesRequestedRef.current = true;
        void useAddressStore.getState().fetchCities();
      }
      return;
    }

    const preferred = pickDefaultAddress(addresses);
    if (!preferred) return;

    doneRef.current = true;
    // Сбой проверки зоны здесь не должен ничего ломать: человек просто увидит
    // привычный запрос адреса.
    void applySavedAddress(preferred).catch(() => { /* ignore */ });
  }, [addresses, hasHydrated, selectedCity]);

  return null;
}
